import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { AddonSetting, RateCardSnapshot } from '../types'
import { ADDON_FORM_DESCRIPTION, ChangeAddonForm } from './ChangeAddonForm'
import {
  ChangeRateCardForm,
  RATE_CARD_FORM_DESCRIPTION,
} from './ChangeRateCardForm'

type RateCardTab = 'rate-card' | 'add-ons'

const DESCRIPTIONS: Record<RateCardTab, string> = {
  'rate-card': RATE_CARD_FORM_DESCRIPTION,
  'add-ons': ADDON_FORM_DESCRIPTION,
}

interface RateCardSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The org's card in force today, shown as each rate field's hint. */
  rateCard: RateCardSnapshot | null
  /** The add-ons on for the current month, shown as the percent's hint. */
  addons: AddonSetting[]
}

/** The side sheet where Careforce staff change the organization's rate card
 *  or its add-ons, a tab each. Laid out like the call log details sheet. It
 *  stays open while a change is being written, and closes once it lands. */
export function RateCardSheet({
  open,
  onOpenChange,
  rateCard,
  addons,
}: RateCardSheetProps) {
  // Closing mid-write would unmount the form before it can show the outcome.
  const [writing, setWriting] = useState(false)
  const [tab, setTab] = useState<RateCardTab>('rate-card')

  function onDone() {
    setWriting(false)
    onOpenChange(false)
  }

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next && writing) return
        onOpenChange(next)
      }}>
      <SheetContent
        className="bg-neutral-ui-bg overflow-y-auto gap-0 p-0 w-full max-w-none sm:max-w-lg rounded-l-xl"
        showCloseButton={false}>
        <SheetHeader className="bg-neutral-ui-bg sticky top-0 z-10">
          <div className="flex items-start justify-between gap-2">
            <SheetTitle className="text-left text-lg">
              Change rate card &amp; add-ons
            </SheetTitle>
            {!writing && (
              <SheetClose asChild>
                <Button size="icon" variant="ghost" className="h-7 w-7">
                  <X className="h-5 w-5" />
                  <span className="sr-only">Close</span>
                </Button>
              </SheetClose>
            )}
          </div>
          <SheetDescription>{DESCRIPTIONS[tab]}</SheetDescription>
        </SheetHeader>

        <Tabs
          value={tab}
          onValueChange={(next) => {
            if (!writing) setTab(next as RateCardTab)
          }}
          className="gap-0">
          <TabsList
            variant="line"
            className="w-full shrink-0 gap-7 border-b border-neutral-subtle-border px-4">
            <TabsTrigger
              value="rate-card"
              disabled={writing}
              className="cursor-pointer px-0.5 text-[13px]">
              Rate card
            </TabsTrigger>
            <TabsTrigger
              value="add-ons"
              disabled={writing}
              className="cursor-pointer px-0.5 text-[13px]">
              Add-ons
            </TabsTrigger>
          </TabsList>
          {/* forceMount keeps each tab's inputs, reason and preview across
              tab switches; TabsContent hides the inactive one. */}
          <TabsContent
            value="rate-card"
            forceMount
            className="px-4 pt-4 pb-4 data-[state=active]:block">
            <ChangeRateCardForm
              rateCard={rateCard}
              writing={writing}
              setWriting={setWriting}
              onDone={onDone}
            />
          </TabsContent>
          <TabsContent
            value="add-ons"
            forceMount
            className="px-4 pt-4 pb-4 data-[state=active]:block">
            <ChangeAddonForm
              addons={addons}
              writing={writing}
              setWriting={setWriting}
              onDone={onDone}
            />
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}
